import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';

function OnboardingStep({ number, icon, title, desc, to }) {
  const { t } = useTranslation();
  const inner = (
    <div style={{
      border: '1px solid var(--bs-border-color)',
      borderRadius: 'var(--radius-card)',
      padding: '1.25rem',
      width: 160,
      background: 'var(--bs-body-bg)',
      transition: 'box-shadow 0.16s',
      textDecoration: 'none',
      color: 'var(--bs-body-color)',
      display: 'block',
      cursor: to ? 'pointer' : 'default',
      opacity: to ? 1 : 0.5,
    }}>
      <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{icon}</div>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--accent-text)', marginBottom: '0.25rem' }}>
        {t('dashboard.step', 'Step')} {number}
      </div>
      <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{title}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', lineHeight: 1.4 }}>{desc}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{inner}</Link> : inner;
}

function riskClass(score) {
  if (score == null) return 'unknown';
  if (score >= 81) return 'low';
  if (score >= 61) return 'medium';
  return 'high';
}

function riskLabel(score) {
  if (score == null) return 'Unknown';
  if (score >= 81) return 'Low';
  if (score >= 61) return 'Medium';
  if (score >= 31) return 'High';
  return 'Critical';
}

function riskColor(score) {
  if (score == null) return '#94a3b8';
  if (score >= 81) return '#10b981';
  if (score >= 61) return '#f59e0b';
  return '#ef4444';
}

function formatRatio(value, total) {
  if (!total) return '0/0';
  return `${value}/${total}`;
}

function Dashboard() {
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repoCount, setRepoCount] = useState(0);
  const [sort, setSort] = useState('risk');
  const [craFilter, setCraFilter] = useState('all');
  const [dashboardError, setDashboardError] = useState('');
  const [dashboardWarning, setDashboardWarning] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setCards([]);
      setDashboardError('');
      setDashboardWarning('');
      try {
        const [reposRes, statsRes] = await Promise.allSettled([
          axios.get(`${config.BASE_URL}/repositories/local`),
          axios.get(`${config.BASE_URL}/portfolio/stats`),
        ]);

        if (reposRes.status !== 'fulfilled') {
          throw new Error(reposRes.reason?.response?.data?.error || reposRes.reason?.message || 'Failed to load repositories.');
        }

        const repos = reposRes.value.data || [];
        const portfolioStats = statsRes.status === 'fulfilled' ? (statsRes.value.data || {}) : {};
        if (cancelled) return;

        if (statsRes.status !== 'fulfilled') {
          setDashboardWarning(t(
            'dashboard.partialPortfolioWarning',
            'Portfolio summary data could not be fully loaded. Repository cards may show partial results.'
          ));
        }

        setRepoCount(repos.length);

        if (repos.length === 0) {
          setCards([]);
          setLoading(false);
          return;
        }

        setCards(repos.map(repoName => {
          const stats = portfolioStats[repoName] || {};
          return {
            repoName,
            hasSbom: stats.hasSbom ?? false,
            loadingSbom: false,
            healthLoading: false,
            sbomId: stats.commitHash ? `${stats.commitHash}-sbom.json` : null,
            lastAnalysed: stats.lastScanned || null,
            packages: stats.totalPackages ?? null,
            cves: stats.vulns?.total ?? null,
            cvesCritical: stats.vulns?.critical ?? 0,
            cvesHigh: stats.vulns?.high ?? 0,
            anomalyCount: stats.anomalies?.count ?? 0,
            highAnomalies: stats.anomalies?.high ?? 0,
            abandoned: stats.abandoned ?? 0,
            malicious: stats.malicious ?? 0,
            malwareScanned: Boolean(stats.hasSbom),
            riskScore: stats.riskScore ?? null,
            policyStatus: stats.policyStatus || null,
            qualityGrade: stats.quality?.grade || null,
            qualityScore: stats.quality?.score ?? null,
            craGrade: stats.cra?.grade || null,
            craScore: stats.cra?.score ?? null,
            identifierTotalPackages: stats.identifiers?.totalPackages ?? 0,
            packagesWithPurl: stats.identifiers?.packagesWithPurl ?? 0,
            packagesWithCpe: stats.identifiers?.packagesWithCpe ?? 0,
            packagesMissingBothIds: stats.identifiers?.packagesMissingBothIds ?? 0,
            remediationTracked: stats.remediation?.tracked ?? 0,
            remediationOverdue: stats.remediation?.overdue ?? 0,
            remediationAcceptedRisk: stats.remediation?.acceptedRisk ?? 0,
            remediationSuppressed: stats.remediation?.suppressed ?? 0,
            remediationUnassignedCritical: stats.remediation?.unassignedCritical ?? 0,
            reachabilityScanned: Boolean(stats.reachability?.scanned),
            reachabilityScannedAt: stats.reachability?.scannedAt || null,
            reachableVulns: stats.reachability?.reachable ?? 0,
            notAffectedVulns: stats.reachability?.notAffected ?? 0,
            investigatingVulns: stats.reachability?.underInvestigation ?? 0,
            supplierTrustScore: stats.supplierTrust?.score ?? null,
            supplierTrustTier: stats.supplierTrust?.tier || null,
            slsaVerified: Boolean(stats.supplierTrust?.slsaVerified),
            slsaLevel: stats.supplierTrust?.slsaLevel || null,
            partialData: false,
            loadError: false,
          };
        }));
        setLoading(false);
      } catch (e) {
        setRepoCount(0);
        setCards([]);
        setDashboardError(e.message || t('dashboard.loadError', 'Failed to load the dashboard.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [t]);

  const summary = useMemo(() => {
    const analysed = cards.filter(c => c.hasSbom);
    const totalCves = analysed.reduce((s, c) => s + (c.cves || 0), 0);
    const totalPackages = analysed.reduce((s, c) => s + (c.packages || 0), 0);
    const highRisk = analysed.filter(c => c.riskScore != null && c.riskScore < 61).length;
    const criticalCves = analysed.reduce((s, c) => s + (c.cvesCritical || 0), 0);
    const totalMalware = analysed.reduce((s, c) => s + (c.malicious || 0), 0);
    const totalAnomalies = analysed.reduce((s, c) => s + (c.anomalyCount || 0), 0);
    const totalReachable = analysed.reduce((s, c) => s + (c.reachableVulns || 0), 0);
    const totalNotAffected = analysed.reduce((s, c) => s + (c.notAffectedVulns || 0), 0);
    const reachabilityCoverage = analysed.filter(c => c.reachabilityScanned).length;
    const craReady = analysed.filter(c => (c.craScore ?? 0) >= 75).length;
    const identifierGaps = analysed.reduce((s, c) => s + (c.packagesMissingBothIds || 0), 0);
    const remediationOverdue = analysed.reduce((s, c) => s + (c.remediationOverdue || 0), 0);
    const acceptedRisk = analysed.reduce((s, c) => s + (c.remediationAcceptedRisk || 0), 0);
    const supplierRisk = analysed.filter(c => (c.supplierTrustScore ?? 100) < 65).length;
    return { repos: repoCount, totalCves, totalPackages, highRisk, criticalCves, totalMalware, totalAnomalies, totalReachable, totalNotAffected, reachabilityCoverage, craReady, identifierGaps, remediationOverdue, acceptedRisk, supplierRisk };
  }, [cards, repoCount]);

  const sortedCards = [...cards].sort((a, b) => {
    if (sort === 'name') return a.repoName.localeCompare(b.repoName);
    if (sort === 'date') {
      const da = a.lastAnalysed ? new Date(a.lastAnalysed) : 0;
      const db = b.lastAnalysed ? new Date(b.lastAnalysed) : 0;
      return db - da;
    }
    if (sort === 'cra_desc') {
      const ca = a.craScore ?? -1;
      const cb = b.craScore ?? -1;
      return cb - ca;
    }
    if (sort === 'cra_asc') {
      const ca = a.craScore ?? Number.MAX_SAFE_INTEGER;
      const cb = b.craScore ?? Number.MAX_SAFE_INTEGER;
      return ca - cb;
    }
    // risk: lower score = higher risk = first, unknown scores last
    const ra = a.riskScore ?? Number.MAX_SAFE_INTEGER;
    const rb = b.riskScore ?? Number.MAX_SAFE_INTEGER;
    return ra - rb;
  });

  const analysedCards = sortedCards.filter(c => c.hasSbom === true);
  const craFilteredCards = sortedCards.filter(card => {
    if (craFilter === 'all') return true;
    if (craFilter === 'ready') return (card.craScore ?? -1) >= 75;
    if (craFilter === 'at_risk') return card.hasSbom === true && card.craScore != null && card.craScore < 75;
    if (craFilter === 'missing') return card.hasSbom === true && card.craScore == null;
    return true;
  });
  const visibleCards = craFilteredCards.filter(c => c.hasSbom !== false);
  const unanalysedCards = sortedCards.filter(c => c.hasSbom === false);
  const partialCardsCount = cards.filter(c => c.partialData || c.loadError).length;

  return (
    <div className="App-body">
      {/* Page header */}
      <div className="ce-dashboard-header">
        <div>
          <h1 className="ce-page-title">📊 {t('dashboard.title')}</h1>
          <p className="ce-page-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="ce-filter-bar">
          <select
            className="ce-filter-select"
            value={sort}
            onChange={e => setSort(e.target.value)}
          >
            <option value="risk">{t('dashboard.sort.risk_desc')}</option>
            <option value="cra_desc">{t('dashboard.sort.cra_desc', 'Highest CRA first')}</option>
            <option value="cra_asc">{t('dashboard.sort.cra_asc', 'Lowest CRA first')}</option>
            <option value="name">{t('dashboard.sort.name_asc')}</option>
            <option value="date">{t('dashboard.sortBy')} date</option>
          </select>
          <select
            className="ce-filter-select"
            value={craFilter}
            onChange={e => setCraFilter(e.target.value)}
          >
            <option value="all">{t('dashboard.craFilter.all', 'All CRA states')}</option>
            <option value="ready">{t('dashboard.craFilter.ready', 'CRA ready')}</option>
            <option value="at_risk">{t('dashboard.craFilter.atRisk', 'CRA at risk')}</option>
            <option value="missing">{t('dashboard.craFilter.missing', 'CRA missing')}</option>
          </select>
        </div>
      </div>

      {/* Stats grid — skeletons during load, real cards when data arrives */}
      <div style={{ marginBottom: '1.75rem' }}>
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="ce-kpi">
              <div className="sk-base" style={{ width: 24, height: 14, borderRadius: 4, marginBottom: 8 }} />
              <div className="sk-base" style={{ width: '55%', height: 28, borderRadius: 6, marginBottom: 6 }} />
              <div className="sk-base" style={{ width: '80%', height: 11, borderRadius: 4 }} />
            </div>
          ))
        ) : summary.repos > 0 ? (
          <>
            {[
              {
                label: 'Portfolio',
                kpis: [
                  { label: t('dashboard.repositories_label', 'Repositories'), value: summary.repos, icon: '🗂️', color: undefined },
                  { label: t('dashboard.analyzed', 'Analyzed'), value: `${analysedCards.length}/${summary.repos}`, icon: '🔬', color: '#6366f1' },
                  { label: t('dashboard.totalPackages', 'Total Packages'), value: summary.totalPackages, icon: '📦', color: undefined },
                ],
              },
              {
                label: 'Vulnerability Exposure',
                kpis: [
                  { label: t('dashboard.totalCves', 'Total CVEs'), value: summary.totalCves, icon: '🛡️', color: summary.totalCves > 0 ? '#ef4444' : '#10b981' },
                  { label: t('dashboard.critical', 'Critical'), value: summary.criticalCves, icon: '🔴', color: summary.criticalCves > 0 ? '#ef4444' : undefined },
                  { label: t('dashboard.highRiskRepos', 'High-Risk Projects'), value: summary.highRisk, icon: '🚨', color: summary.highRisk > 0 ? '#f59e0b' : '#10b981' },
                  { label: t('dashboard.reachableCves', 'Reachable CVEs'), value: summary.totalReachable ?? 0, icon: '🧭', color: (summary.totalReachable ?? 0) > 0 ? '#ef4444' : '#10b981' },
                  { label: t('dashboard.notAffected', 'Not Affected'), value: summary.totalNotAffected ?? 0, icon: '🟢', color: (summary.totalNotAffected ?? 0) > 0 ? '#10b981' : undefined },
                  { label: t('dashboard.reachabilityScanned', 'Reachability'), value: `${summary.reachabilityCoverage ?? 0}/${analysedCards.length}`, icon: '🛣️', color: (summary.reachabilityCoverage ?? 0) > 0 ? '#2563eb' : undefined },
                ],
              },
              {
                label: 'Supply Chain Threats',
                kpis: [
                  { label: t('dashboard.malware', 'Malware'), value: summary.totalMalware ?? 0, icon: '🦠', color: (summary.totalMalware ?? 0) > 0 ? '#ef4444' : '#10b981' },
                  { label: t('dashboard.maintainerAnomalies', 'Maintainer Anomalies'), value: summary.totalAnomalies ?? 0, icon: '🕵️', color: (summary.totalAnomalies ?? 0) > 0 ? '#f97316' : '#10b981' },
                  { label: t('dashboard.supplierRisk', 'Supplier Trust at Risk'), value: summary.supplierRisk ?? 0, icon: '🤝', color: (summary.supplierRisk ?? 0) > 0 ? '#f59e0b' : '#10b981' },
                ],
              },
              {
                label: 'Compliance & Hygiene',
                kpis: [
                  { label: t('dashboard.craReady', 'CRA Ready'), value: `${summary.craReady ?? 0}/${analysedCards.length}`, icon: '🇪🇺', color: (summary.craReady ?? 0) > 0 ? '#2563eb' : undefined },
                  { label: t('dashboard.identifierGaps', 'Identifier Gaps'), value: summary.identifierGaps ?? 0, icon: '🪪', color: (summary.identifierGaps ?? 0) > 0 ? '#f59e0b' : '#10b981' },
                  { label: t('dashboard.remediationOverdue', 'Remediation Overdue'), value: summary.remediationOverdue ?? 0, icon: '⏰', color: (summary.remediationOverdue ?? 0) > 0 ? '#ef4444' : '#10b981' },
                  { label: t('dashboard.acceptedRisk', 'Accepted Risk'), value: summary.acceptedRisk ?? 0, icon: '⚫', color: (summary.acceptedRisk ?? 0) > 0 ? '#64748b' : undefined },
                ],
              },
            ].map(group => (
              <div key={group.label} className="ce-kpi-group">
                <div className="ce-kpi-group-label">{group.label}</div>
                <div className="ce-kpi-row">
                  {group.kpis.map(card => (
                    <div key={card.label} className="ce-kpi" style={card.color ? { '--kpi-color': card.color } : undefined}>
                      <div className="ce-kpi-value">{card.icon} {card.value}</div>
                      <div className="ce-kpi-label">{card.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : null}
      </div>

      {/* Loading — show skeleton repo cards */}
      {loading && cards.length === 0 && (
        <div className="ce-repo-grid" style={{ marginBottom: '2rem' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="ce-repo-card" style={{ cursor: 'default' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div className="sk-base" style={{ width: '50%', height: 16, borderRadius: 5 }} />
                <div className="sk-base" style={{ width: 52, height: 16, borderRadius: 100 }} />
              </div>
              <div className="sk-base" style={{ width: '35%', height: 11, borderRadius: 4, marginBottom: '0.75rem' }} />
              <div className="sk-base" style={{ width: '100%', height: 4, borderRadius: 2, marginBottom: '0.75rem' }} />
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--bs-border-color)' }}>
                {[40, 30, 35].map((w, j) => (
                  <div key={j}>
                    <div className="sk-base" style={{ width: w, height: 16, borderRadius: 4, marginBottom: 4 }} />
                    <div className="sk-base" style={{ width: w - 5, height: 10, borderRadius: 3 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && dashboardError && (
        <EmptyState
          icon="⚠️"
          title={t('dashboard.loadErrorTitle', 'Could not load the dashboard')}
          description={dashboardError || t('dashboard.loadError', 'Failed to load the dashboard.')}
          action={<button className="btn btn-primary" onClick={() => window.location.reload()}>{t('dashboard.retry', 'Retry')}</button>}
        />
      )}

      {!loading && !dashboardError && dashboardWarning && (
        <div className="alert alert-warning">{dashboardWarning}</div>
      )}

      {!loading && !dashboardError && partialCardsCount > 0 && (
        <div className="alert alert-warning">
          {t(
            'dashboard.partialRepoWarning',
            '{{count}} repository summaries could not be fully loaded. Open a repository to review or retry the missing analysis data.',
            { count: partialCardsCount }
          )}
        </div>
      )}

      {/* Onboarding / Empty state */}
      {!loading && !dashboardError && cards.length === 0 && analysedCards.length === 0 && unanalysedCards.length === 0 && (
        <EmptyState
          icon="🛡️"
          title={t('dashboard.welcomeTitle', 'Welcome to DepGuard')}
          description={t('dashboard.welcomeDesc', 'Start by uploading an SBOM or adding a repository to analyze your dependency security posture.')}
          action={
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              <OnboardingStep number={1} icon="⬆️" title={t('dashboard.uploadSbom', 'Upload SBOM')} desc={t('dashboard.uploadSbomDesc', 'Upload a CycloneDX or SPDX bill of materials')} to="/upload" />
              <OnboardingStep number={2} icon="🔬" title={t('dashboard.scanAnalyze', 'Scan & Analyze')} desc={t('dashboard.scanAnalyzeDesc', 'Detect vulnerabilities, license issues, and SBOM quality issues')} to={null} />
              <OnboardingStep number={3} icon="📊" title={t('dashboard.viewInsights', 'View Insights')} desc={t('dashboard.viewInsightsDesc', 'Risk scores, trends, policy compliance')} to={null} />
              <div style={{ width: '100%', display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
                <Link to="/upload" className="btn btn-primary">{t('dashboard.uploadFirst', '⬆️ Upload your first SBOM')}</Link>
                <Link to="/repos" className="btn btn-outline-secondary">{t('dashboard.browseRepos', '🗂️ Browse Repositories')}</Link>
              </div>
            </div>
          }
        />
      )}

      {/* Main repo grid */}
      {!dashboardError && visibleCards.length > 0 && (
        <>
          <div className="ce-section-header">
            <span className="ce-section-title">
              {t('dashboard.repositories', 'Repositories')}
              {craFilter !== 'all' && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: 'var(--bs-secondary-color)', fontWeight: 500 }}>
                  ({visibleCards.length})
                </span>
              )}
            </span>
          </div>
          <div className="ce-repo-grid" style={{ marginBottom: '2rem' }}>
            {visibleCards.map(card => (
              <div
                key={card.repoName}
                className={`ce-repo-card risk-${riskClass(card.riskScore)}`}
                onClick={() => navigate(`/RepoDetails/${card.repoName}`)}
              >
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div className="ce-repo-name" style={{ maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {card.repoName}
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexShrink: 0 }}>
                    {card.qualityGrade && (
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.15rem 0.45rem',
                        borderRadius: 4, border: '1px solid var(--bs-border-color)',
                        color: card.qualityGrade === 'A' ? '#10b981' : card.qualityGrade === 'B' ? '#6366f1' : card.qualityGrade === 'C' ? '#f59e0b' : '#ef4444',
                      }}>
                        {card.qualityGrade}
                      </span>
                    )}
                    {card.craGrade && (
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.15rem 0.45rem',
                        borderRadius: 4, border: '1px solid rgba(37,99,235,.25)', color: '#2563eb',
                      }}>
                        CRA {card.craGrade}
                      </span>
                    )}
                    {card.supplierTrustTier && (
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 700, padding: '0.15rem 0.45rem',
                        borderRadius: 4, border: '1px solid rgba(148,163,184,.28)',
                        color: card.supplierTrustTier === 'trusted' ? '#10b981' : card.supplierTrustTier === 'watch' ? '#2563eb' : card.supplierTrustTier === 'risky' ? '#f59e0b' : '#ef4444',
                      }}>
                        Trust {card.supplierTrustTier}
                      </span>
                    )}
                    <span className={`ce-risk-badge ${riskClass(card.riskScore)}`}>
                      {(card.loadingSbom || card.healthLoading) ? 'Loading…' : ((card.partialData || card.loadError) ? 'Partial' : riskLabel(card.riskScore))}
                    </span>
                  </div>
                </div>

                {/* Last analysed */}
                <div className="ce-repo-meta" style={{ marginBottom: '0.625rem' }}>
                  {card.loadingSbom
                    ? 'Checking SBOM…'
                    : (card.partialData || card.loadError) && !card.lastAnalysed
                    ? 'Could not fully load repository summary'
                    : card.lastAnalysed
                    ? `Analyzed ${new Date(card.lastAnalysed).toLocaleDateString()}`
                    : 'Not yet analyzed'}
                </div>

                {/* Risk score bar */}
                <div style={{ marginBottom: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6875rem', color: 'var(--bs-secondary-color)', marginBottom: '0.25rem' }}>
                    <span>{t('dashboard.riskScore_label', 'Risk Score')}</span>
                      <span style={{ fontWeight: 600, color: riskColor(card.riskScore) }}>
                        {(card.loadingSbom || card.healthLoading) ? '…' : (card.riskScore ?? '—')}/100
                      </span>
                  </div>
                  <div className="ce-risk-bar">
                    <div className="ce-risk-bar-fill" style={{ width: `${card.riskScore ?? 0}%`, background: riskColor(card.riskScore) }} />
                  </div>
                </div>

                {/* Signal grid — key metrics in 2-col layout */}
                <div className="ce-card-signals">
                  <div className="ce-card-signal">
                    <span>📦</span>
                    <span style={{ color: 'var(--bs-body-color)' }}>
                      {(card.loadingSbom || card.healthLoading) ? '…' : (card.packages ?? '—')}
                    </span>
                    <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400 }}>pkgs</span>
                  </div>
                  <div className="ce-card-signal" style={(card.cves ?? 0) > 0 ? { color: '#ef4444' } : undefined}>
                    <span>🛡️</span>
                    <span>{card.cves ?? '—'}</span>
                    {card.cvesCritical > 0 && <span style={{ color: '#ef4444' }}>{card.cvesCritical}C</span>}
                    {card.cvesHigh > 0 && <span style={{ color: '#f59e0b' }}>{card.cvesHigh}H</span>}
                    <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400 }}>CVEs</span>
                  </div>
                  {card.craGrade && (
                    <div className="ce-card-signal" style={{ color: '#2563eb' }}>
                      <span>🇪🇺</span>
                      <span>CRA {card.craGrade}{card.craScore != null ? ` ${card.craScore}/100` : ''}</span>
                    </div>
                  )}
                  {(card.malicious ?? 0) > 0 && (
                    <div className="ce-card-signal" style={{ color: '#ef4444' }}>
                      <span>🦠</span>
                      <span>{card.malicious} malware</span>
                    </div>
                  )}
                  {(card.anomalyCount ?? 0) > 0 && (
                    <div className="ce-card-signal" style={{ color: (card.highAnomalies ?? 0) > 0 ? '#ef4444' : '#f59e0b' }}>
                      <span>🕵️</span>
                      <span>{card.anomalyCount} anomal{card.anomalyCount === 1 ? 'y' : 'ies'}</span>
                    </div>
                  )}
                  {card.supplierTrustScore != null && (
                    <div className="ce-card-signal" style={{ color: (card.supplierTrustScore ?? 100) < 65 ? '#f59e0b' : '#10b981' }}>
                      <span>🤝</span>
                      <span>Trust {card.supplierTrustScore}/100</span>
                    </div>
                  )}
                  {card.reachabilityScanned && (
                    <div className="ce-card-signal" style={{ color: (card.reachableVulns ?? 0) > 0 ? '#ef4444' : '#10b981' }}>
                      <span>🧭</span>
                      <span>
                        {(card.reachableVulns ?? 0) > 0
                          ? `${card.reachableVulns} reachable`
                          : `${card.notAffectedVulns ?? 0} not affected`}
                      </span>
                    </div>
                  )}
                  {(card.packagesMissingBothIds ?? 0) > 0 && (
                    <div className="ce-card-signal" style={{ color: '#f59e0b' }}>
                      <span>🪪</span>
                      <span>{card.packagesMissingBothIds} ID gap{card.packagesMissingBothIds !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {(card.remediationOverdue ?? 0) > 0 && (
                    <div className="ce-card-signal" style={{ color: '#ef4444' }}>
                      <span>⏰</span>
                      <span>{card.remediationOverdue} overdue</span>
                    </div>
                  )}
                  {(card.remediationAcceptedRisk ?? 0) > 0 && (
                    <div className="ce-card-signal" style={{ color: '#64748b' }}>
                      <span>⚫</span>
                      <span>{card.remediationAcceptedRisk} accepted risk</span>
                    </div>
                  )}
                  {card.abandoned > 0 && (
                    <div className="ce-card-signal" style={{ color: '#f59e0b' }}>
                      <span>👻</span>
                      <span>{card.abandoned} abandoned</span>
                    </div>
                  )}
                </div>

                {/* Scan coverage pills */}
                <div className="ce-scan-pills">
                  <span className={`ce-scan-pill ${card.hasSbom ? 'ce-scan-pill--ok' : 'ce-scan-pill--muted'}`}>
                    {card.loadingSbom ? '⏳ SBOM' : '✓ SBOM'}
                  </span>
                  {card.cves != null && (
                    <span className={`ce-scan-pill ${card.cves > 0 ? 'ce-scan-pill--warn' : 'ce-scan-pill--ok'}`}>
                      ✓ Vulns
                    </span>
                  )}
                  {card.qualityGrade && (
                    <span className="ce-scan-pill ce-scan-pill--info">
                      ✓ Quality {card.qualityGrade}
                    </span>
                  )}
                  {card.policyStatus && (
                    <span className={`ce-scan-pill ${card.policyStatus === 'pass' ? 'ce-scan-pill--ok' : 'ce-scan-pill--err'}`}>
                      {card.policyStatus === 'pass' ? '✓' : '✗'} Policy
                    </span>
                  )}
                  {card.slsaLevel && (
                    <span className={`ce-scan-pill ${card.slsaVerified ? 'ce-scan-pill--ok' : 'ce-scan-pill--info'}`}>
                      {card.slsaVerified ? '✓' : ''} SLSA {card.slsaLevel}
                    </span>
                  )}
                  {card.malwareScanned && (card.malicious ?? 0) === 0 && (
                    <span className="ce-scan-pill ce-scan-pill--ok">✓ Malware clean</span>
                  )}
                  {(card.partialData || card.loadError) && (
                    <span className="ce-scan-pill ce-scan-pill--warn">⚠ Partial data</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Unanalysed repos */}
      {!dashboardError && craFilter === 'all' && unanalysedCards.length > 0 && (
        <div>
          <div className="ce-section-header">
            <span className="ce-section-title">
              {unanalysedCards.length} repo{unanalysedCards.length !== 1 ? 's' : ''} without SBOM
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {unanalysedCards.map(c => (
              <div
                key={c.repoName}
                onClick={() => navigate(`/RepoDetails/${c.repoName}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.875rem', borderRadius: 'var(--radius-sm)',
                  border: '1px dashed var(--bs-border-color)',
                  cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--bs-secondary-color)',
                  transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent-text)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--bs-border-color)'; e.currentTarget.style.color = 'var(--bs-secondary-color)'; }}
              >
                <span>📁</span>
                <span>{c.repoName}</span>
                <span style={{ fontSize: '0.625rem', opacity: 0.6 }}>{t('dashboard.noSbom', 'No SBOM')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
